import { type BoxProps, Box } from '@chakra-ui/react';
import { isWistiaEmbed } from 'helpers/videoEmbed';
import { FC, Ref, useEffect, useMemo, useRef } from 'react';

type WistiaPlayerProps = Record<string, string | number | boolean | null | undefined>;

export type WistiaPlaybackRef = HTMLElement & {
  play?: () => Promise<void> | void;
  pause?: () => void;
};

type ParsedScript = {
  attrs: Array<{ name: string; value: string }>;
  inlineCode: string;
};

const injectedScriptKeys = new Set<string>();
const scriptTagPattern = /<script\b[^>]*>[\s\S]*?<\/script>/gi;
const transcriptPattern = /wistia_preload_transcript_(outer_wrapper|text)/i;

const parseScriptAttrs = (rawOpenTag: string): Array<{ name: string; value: string }> => {
  const attrsBlock = rawOpenTag.replace(/^<script\s*/i, '').replace(/>\s*$/i, '');
  const attrs: Array<{ name: string; value: string }> = [];
  const attrPattern = /([a-zA-Z_:][a-zA-Z0-9_:.-]*)(?:\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s"'>]+)))?/g;
  let attrMatch: RegExpExecArray | null = attrPattern.exec(attrsBlock);

  while (attrMatch) {
    const name = attrMatch[1];
    const value = attrMatch[2] ?? attrMatch[3] ?? attrMatch[4] ?? '';
    attrs.push({ name, value });
    attrMatch = attrPattern.exec(attrsBlock);
  }

  return attrs;
};

const parseScriptsFromHtml = (embedHtml: string): ParsedScript[] => {
  const scripts: ParsedScript[] = [];
  let scriptMatch: RegExpExecArray | null = scriptTagPattern.exec(embedHtml);

  while (scriptMatch) {
    const fullTag = scriptMatch[0];
    const openTagMatch = fullTag.match(/^<script\b[^>]*>/i);
    const inlineCodeMatch = fullTag.match(/^<script\b[^>]*>([\s\S]*?)<\/script>$/i);

    scripts.push({
      attrs: parseScriptAttrs(openTagMatch?.[0] || '<script>'),
      inlineCode: inlineCodeMatch?.[1] || '',
    });

    scriptMatch = scriptTagPattern.exec(embedHtml);
  }

  return scripts;
};

const getScriptKey = (script: ParsedScript): string => {
  const srcAttr = script.attrs.find((attr) => attr.name.toLowerCase() === 'src');
  if (srcAttr?.value) return `src:${srcAttr.value}`;
  return `inline:${script.inlineCode}`;
};

const stripScriptTags = (embedHtml: string): string => embedHtml.replace(scriptTagPattern, '');

const escapeHtmlAttribute = (value: string): string =>
  value.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

const toHyphenCase = (value: string): string => value.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase();

const serializePlayerProps = (playerProps?: WistiaPlayerProps): string => {
  if (!playerProps) return '';

  const attrs = Object.entries(playerProps)
    .filter(([, value]) => value !== undefined && value !== null)
    .map(([key, value]) => {
      const normalizedKey = toHyphenCase(key);
      const normalizedValue = String(value);
      return `${normalizedKey}="${escapeHtmlAttribute(normalizedValue)}"`;
    });

  if (!attrs.length) return '';
  return ` ${attrs.join(' ')}`;
};

const applyPlayerPropsToEmbedHtml = (embedHtml: string, playerProps?: WistiaPlayerProps): string => {
  const attrs = serializePlayerProps(playerProps);
  if (!attrs) return embedHtml;

  return embedHtml.replace(/<wistia-player\b([^>]*)>/gi, (match) => {
    if (match.endsWith('/>')) {
      return match.replace(/\/>$/, `${attrs} />`);
    }

    return match.replace(/>$/, `${attrs}>`);
  });
};

const setOptionalRef = <T,>(ref: Ref<T> | undefined, value: T): void => {
  if (!ref) return;

  if (typeof ref === 'function') {
    ref(value);
    return;
  }

  (ref as { current: T }).current = value;
};

type WistiaEmbedFromHtmlProps = {
  embedHtml: string;
  playerProps?: WistiaPlayerProps;
  wistiaRef?: Ref<WistiaPlaybackRef | null>;
} & BoxProps;

export const WistiaEmbedFromHtml: FC<WistiaEmbedFromHtmlProps> = ({
  embedHtml,
  playerProps,
  wistiaRef,
  ...boxProps
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const safeEmbedHtml = useMemo(() => {
    const trimmed = embedHtml?.trim();
    if (!trimmed) return null;

    const htmlWithoutScripts = stripScriptTags(trimmed);
    const htmlWithPlayerProps = applyPlayerPropsToEmbedHtml(htmlWithoutScripts, playerProps);
    const hasTranscript = transcriptPattern.test(htmlWithoutScripts);

    return {
      htmlWithoutScripts: htmlWithPlayerProps,
      hasTranscript,
    };
  }, [embedHtml, playerProps]);

  useEffect(() => {
    if (!safeEmbedHtml) return;
    if (typeof window === 'undefined') return;

    const scripts = parseScriptsFromHtml(embedHtml);
    scripts.forEach((scriptDef) => {
      const key = getScriptKey(scriptDef);
      if (injectedScriptKeys.has(key)) return;

      const scriptEl = document.createElement('script');

      scriptDef.attrs.forEach(({ name, value }) => {
        if (name.toLowerCase() === 'async') {
          scriptEl.async = true;
          return;
        }

        scriptEl.setAttribute(name, value);
      });

      if (!scriptEl.src && scriptDef.inlineCode) {
        scriptEl.text = scriptDef.inlineCode;
      }

      document.head.appendChild(scriptEl);
      injectedScriptKeys.add(key);
    });
  }, [embedHtml, safeEmbedHtml]);

  useEffect(() => {
    if (!wistiaRef) return;

    const playerElement = containerRef.current?.querySelector('wistia-player') as WistiaPlaybackRef | null;
    setOptionalRef(wistiaRef, playerElement);

    return () => {
      setOptionalRef(wistiaRef, null);
    };
  }, [safeEmbedHtml, wistiaRef]);

  if (!safeEmbedHtml || !isWistiaEmbed(embedHtml)) return null;

  return (
    <Box
      ref={containerRef}
      {...boxProps}
      data-wistia-transcript={safeEmbedHtml.hasTranscript ? 'present' : 'absent'}
      dangerouslySetInnerHTML={{ __html: safeEmbedHtml.htmlWithoutScripts }}
    />
  );
};
